import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NonWorkDay } from "util/network/Calendar";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./Calendar.css";
import { toLocalDateStr, PendingRange } from "./useCalendarTab";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type Props = {
  year: number;
  onYearChange: (year: number) => void;
  dayMap: Record<string, NonWorkDay>;
  localChanges: Record<string, boolean>;
  pendingNames: Record<string, string>;
  onTileClick: (date: Date, event: React.MouseEvent) => void;
  pendingRange: PendingRange;
  nameDialogState: { open: boolean; dates: string[]; existingDates: string[]; defaultName: string };
  confirmNameDialog: (name: string, overwriteExisting: boolean) => void;
  cancelNameDialog: () => void;
};

const CalendarTabView = ({
  year,
  onYearChange,
  dayMap,
  localChanges,
  pendingNames,
  onTileClick,
  pendingRange,
  nameDialogState,
  confirmNameDialog,
  cancelNameDialog,
}: Props) => {
  const [dialogName, setDialogName] = useState("");
  const [overwriteExisting, setOverwriteExisting] = useState(true);

  useEffect(() => {
    if (nameDialogState.open) {
      setDialogName(nameDialogState.defaultName);
      setOverwriteExisting(true);
    }
  }, [nameDialogState.open, nameDialogState.defaultName]);
  const getTileClassName = useCallback(
    ({ date, view }: { date: Date; view: string }) => {
      if (view !== "month") return null;
      const dateStr = toLocalDateStr(date);
      const day = dayMap[dateStr];
      const isWorking =
        dateStr in localChanges
          ? localChanges[dateStr]
          : day?.isWorking ?? true;
      if (dateStr in localChanges) {
        if (!isWorking) return "calendar-tile--pending";
        if (day && !day.isWorking) return "calendar-tile--pending-remove";
        return null;
      }
      if (isWorking) return null;
      return day?.type === "specified"
        ? "calendar-tile--specified"
        : "calendar-tile--generated";
    },
    [dayMap, localChanges],
  );

  const pendingRangeStart = pendingRange
    ? toLocalDateStr(pendingRange.start)
    : null;

  const getTileClassNameMerged = useCallback(
    ({ date, view }: { date: Date; view: string }) => {
      const base = getTileClassName({ date, view });
      if (view === "month" && pendingRangeStart === toLocalDateStr(date)) {
        return [base, "calendar-tile--range-start"].filter(Boolean).join(" ");
      }
      return base;
    },
    [getTileClassName, pendingRangeStart],
  );

  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => new Date(year, i, 1)),
    [year],
  );

  return (
    <div style={{ padding: "1rem 1.5rem" }}>
      {/* Year navigation */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          marginBottom: "1rem",
        }}
      >
        <IconButton size="small" onClick={() => onYearChange(year - 1)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          style={{ minWidth: "4rem", textAlign: "center" }}
        >
          {year}
        </Typography>
        <IconButton size="small" onClick={() => onYearChange(year + 1)}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Button
          size="small"
          onClick={() => onYearChange(new Date().getFullYear())}
          variant="outlined"
        >
          Current year
        </Button>
      </div>

      <Dialog
        open={nameDialogState.open}
        onClose={cancelNameDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Name this closed day</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmNameDialog(dialogName, overwriteExisting);
            }}
            margin="dense"
          />
          {nameDialogState.existingDates.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={overwriteExisting}
                  onChange={(e) => setOverwriteExisting(e.target.checked)}
                  size="small"
                />
              }
              label={`Also rename ${nameDialogState.existingDates.length} existing closed day${nameDialogState.existingDates.length > 1 ? "s" : ""}`}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelNameDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => confirmNameDialog(dialogName, overwriteExisting)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* 12-month grid */}
      <div className="calendar-grid">
        {months.map((startDate) => (
          <div key={startDate.toISOString()}>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              style={{ marginBottom: "0.25rem" }}
            >
              {startDate.toLocaleString("default", { month: "long" })}
            </Typography>
            <Calendar
              activeStartDate={startDate}
              showNavigation={false}
              value={null}
              tileClassName={getTileClassNameMerged}
              onClickDay={(date, event) => onTileClick(date, event)}
              tileContent={({ date, view }) => {
                if (view !== "month") return null;
                const dateStr = toLocalDateStr(date);
                const day = dayMap[dateStr];
                const name = pendingNames[dateStr] ?? day?.name ?? null;
                if (!name) return null;
                return (
                  <Tooltip title={name} placement="top" arrow>
                    <span style={{ position: "absolute", inset: 0 }} />
                  </Tooltip>
                );
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarTabView;
